/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { zodResolver } from "@hookform/resolvers/zod"
import { useLastSubmittedUserParameters } from "app/hooks"
import { RootState } from "app/store"
import { Results as ResultsType, setResults, setResultsFresh } from "features/results/ResultsSlice"
import AllCashFlows from "features/userParameters/AllCashFlows"
import Assets, { AssetsType } from "features/userParameters/Assets"
import GeneralParameters from "features/userParameters/GeneralParameters"
import { setLastSubmittedParameters } from "features/userParameters/LastSubmittedParametersSlice"
import {
  initialUserParameters,
  UserParameters,
  userParametersSchema,
} from "features/userParameters/UserParametersSchema"
import XlsxHandling from "features/userParameters/XlsxHandling"
import _ from "lodash"
import { useEffect, useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useDispatch, useSelector } from "react-redux"

export const UserParametersForm = ({
  defaultValues = initialUserParameters,
}: {
  defaultValues?: UserParameters
}) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const lastSubmittedParameters = useLastSubmittedUserParameters()
  const { fresh: areResultsFresh } = useSelector((state: RootState) => state.results)

  const formMethods = useForm<UserParameters>({
    resolver: zodResolver(userParametersSchema),
    defaultValues: defaultValues,
    mode: "onChange",
    // I'd prefer to use shouldUnregister: true, but this causes problems when
    // loading parameters from file
    shouldUnregister: false,
  })

  const { control } = formMethods

  const allocationMode = useWatch({
    control,
    name: "allocationMode",
  })

  const formValues = formMethods.watch()
  useEffect(() => {
    if (allocationMode !== "glide path") {
      formMethods.unregister("finalAssets")
      formMethods.unregister("glidePathLengthMonths")
    }
  }, [allocationMode, formMethods])

  useEffect(() => {
    // we don't want changes to lastSubmittedParameters
    // that occur while loading to mark the results as fresh and thereby
    // clear 'loading' indicators prematurely
    if (!loading) {
      const currentParsedParams = userParametersSchema.safeParse(formValues)

      const fresh =
        currentParsedParams.success && _.isEqual(lastSubmittedParameters, currentParsedParams.data)

      dispatch(setResultsFresh(fresh))
    }
  }, [formValues, dispatch, lastSubmittedParameters, loading])

  useEffect(() => {
    if (areResultsFresh) {
      setLoading(false)
    }
  }, [areResultsFresh])

  const onSubmit = (formData: UserParameters) => {
    // note invalid parameters should always be unequal as last submitted parameters are always valid
    if (lastSubmittedParameters && _.isEqual(formData, lastSubmittedParameters)) {
      return
    }
    setLoading(true)
    dispatch(setLastSubmittedParameters(formData)) // known to be valid at this point

    fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/compute_results`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Failed to submit form data")
        }
        return response.json()
      })
      .then((results: ResultsType) => {
        dispatch(setResults(results))
      })
      .catch((_error: unknown) => {
        // console.error("Submission error:", error)
      })
  }

  return (
    <FormProvider {...formMethods}>
      <form
        id="user-parameters"
        className="pb-3"
        onSubmit={event => {
          void formMethods.handleSubmit(onSubmit)(event)
        }}
      >
        <GeneralParameters />
        <Assets
          legend={allocationMode === "static" ? "Assets" : "Initial Assets"}
          assetsType={AssetsType.Initial}
        />
        {allocationMode === "glide path" && (
          <Assets legend="Final Assets" assetsType={AssetsType.Final} />
        )}
        <AllCashFlows />
        <XlsxHandling />
        <div className="row">
          <div className="ps-4 col-12 col-sm-6 col-md-4 col-lg-3 my-2">
            <button type="submit" className="btn btn-primary" id="submit-button" disabled={loading}>
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="visually-hidden">Loading...</span>
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default UserParametersForm
